import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserCheck, Calendar, DollarSign, Star, TrendingUp, AlertCircle,
  Search, Ban, CheckCircle2, Clock, Plus, Trash2, Award, Flame
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('khadam_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Stat = ({ icon: Icon, label, value, color = 'blue', hint }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
        <Icon size={18} className={`text-${color}-600`} />
      </div>
    </div>
    <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [providers, setProviders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/overview`, { headers: authHeaders() });
    setOverview(data);
  }, []);

  const fetchProviders = useCallback(async () => {
    const params = {};
    if (filter) params.status = filter;
    if (search) params.q = search;
    const { data } = await axios.get(`${API}/admin/providers`, { headers: authHeaders(), params });
    setProviders(data);
  }, [filter, search]);

  const fetchBookings = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/bookings`, { headers: authHeaders() });
    setBookings(data);
  }, []);

  const fetchReviews = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/reviews`, { headers: authHeaders() });
    setReviews(data);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([fetchOverview(), fetchProviders(), fetchBookings(), fetchReviews()])
      .catch((e) => setError(e.response?.data?.detail || 'Erreur admin'))
      .finally(() => setLoading(false));
  }, [isAuthenticated, user, fetchOverview, fetchProviders, fetchBookings, fetchReviews]);

  const updateProvider = async (id, changes) => {
    try {
      await axios.patch(`${API}/admin/providers/${id}`, changes, { headers: authHeaders() });
      fetchProviders();
      fetchOverview();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: authHeaders() });
      fetchReviews();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white pt-20 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Accès administrateur requis</h2>
          <p className="text-sm text-gray-500 mb-4">Connectez-vous avec un compte administrateur.</p>
          <button
            onClick={() => navigate('/')}
            data-testid="admin-go-home"
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl px-6 py-3 transition"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
    { id: 'providers', label: 'Prestataires', icon: Users },
    { id: 'bookings', label: 'Réservations', icon: Calendar },
    { id: 'reviews', label: 'Avis', icon: Star },
  ];

  const statusBadge = (s) => {
    const map = {
      trial: { bg: 'bg-green-100', fg: 'text-green-700', label: 'Essai' },
      active: { bg: 'bg-blue-100', fg: 'text-blue-700', label: 'Actif' },
      paid: { bg: 'bg-indigo-100', fg: 'text-indigo-700', label: 'Payant' },
      suspended: { bg: 'bg-red-100', fg: 'text-red-700', label: 'Suspendu' },
    }[s] || { bg: 'bg-gray-100', fg: 'text-gray-700', label: s || '—' };
    return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${map.bg} ${map.fg}`}>{map.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-gray-500 mb-1">Administration</p>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-title">Panneau Administrateur</h1>
          </div>
          <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-full">
            <span className="text-xs font-bold text-red-700 uppercase">Admin · {user?.email}</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                data-testid={`tab-${tb.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition ${
                  tab === tb.id ? 'bg-blue-500 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-100'
                }`}
              >
                <Icon size={16} />
                {tb.label}
              </button>
            );
          })}
        </div>

        {/* Overview */}
        {tab === 'overview' && overview && (
          <div className="space-y-6" data-testid="overview-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Users} label="Utilisateurs" value={overview.users.total} color="blue" hint={`${overview.users.clients} clients · ${overview.users.providers} prestataires`} />
              <Stat icon={UserCheck} label="Essais actifs" value={overview.providers.active_trials} color="green" hint={`sur ${overview.providers.total} prestataires`} />
              <Stat icon={Calendar} label="Réservations" value={overview.bookings.total} color="indigo" hint={`${overview.bookings.today} aujourd'hui`} />
              <Stat icon={DollarSign} label="GMV total" value={`${overview.bookings.gmv} DH`} color="emerald" />
              <Stat icon={TrendingUp} label="Commission encaissée" value={`${overview.revenue.total_commission} DH`} color="orange" />
              <Stat icon={Star} label="Avis totaux" value={overview.reviews.total} color="yellow" />
            </div>
          </div>
        )}

        {/* Providers */}
        {tab === 'providers' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100" data-testid="providers-panel">
            <div className="p-5 border-b border-gray-100 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher nom, email, ville..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchProviders()}
                  data-testid="providers-search"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <select value={filter} onChange={(e) => { setFilter(e.target.value); setTimeout(fetchProviders, 0); }} data-testid="providers-filter" className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                <option value="">Tous les statuts</option>
                <option value="trial">Essai</option>
                <option value="active">Actif</option>
                <option value="paid">Payant</option>
                <option value="suspended">Suspendu</option>
              </select>
              <button onClick={fetchProviders} className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600">Filtrer</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Prestataire</th>
                    <th className="text-left px-4 py-3 font-semibold">Ville</th>
                    <th className="text-left px-4 py-3 font-semibold">Tarif</th>
                    <th className="text-left px-4 py-3 font-semibold">Statut</th>
                    <th className="text-left px-4 py-3 font-semibold">Essai</th>
                    <th className="text-left px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {providers.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`provider-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.photo} alt={p.name} className="w-9 h-9 rounded-full object-cover" />
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1">
                              {p.name}
                              {p.is_pro && <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-bold">PRO</span>}
                              {p.is_top && <Flame size={12} className="text-orange-500" />}
                            </div>
                            <div className="text-xs text-gray-500">{p.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.city}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{p.price} DH</td>
                      <td className="px-4 py-3">{statusBadge(p.account_status)}</td>
                      <td className="px-4 py-3">
                        {p.trial_active ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium">
                            <Clock size={12} /> {p.days_remaining}j
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => updateProvider(p.id, { extend_trial_days: 30 })}
                            data-testid={`extend-${p.id}`}
                            title="Prolonger essai +30j"
                            className="text-xs px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded font-medium"
                          >
                            <Plus size={12} className="inline" /> 30j
                          </button>
                          <button
                            onClick={() => updateProvider(p.id, { is_pro: !p.is_pro })}
                            data-testid={`pro-${p.id}`}
                            className="text-xs px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded font-medium"
                            title="Toggle PRO"
                          >
                            <Award size={12} className="inline" /> PRO
                          </button>
                          <button
                            onClick={() => updateProvider(p.id, { is_top: !p.is_top })}
                            data-testid={`top-${p.id}`}
                            className="text-xs px-2 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded font-medium"
                            title="Toggle TOP"
                          >
                            <Flame size={12} className="inline" /> Top
                          </button>
                          {p.account_status !== 'suspended' ? (
                            <button
                              onClick={() => updateProvider(p.id, { account_status: 'suspended' })}
                              data-testid={`suspend-${p.id}`}
                              className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded font-medium"
                            >
                              <Ban size={12} className="inline" /> Suspendre
                            </button>
                          ) : (
                            <button
                              onClick={() => updateProvider(p.id, { account_status: 'active' })}
                              data-testid={`reactivate-${p.id}`}
                              className="text-xs px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded font-medium"
                            >
                              <CheckCircle2 size={12} className="inline" /> Réactiver
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {providers.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Aucun prestataire</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bookings */}
        {tab === 'bookings' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto" data-testid="bookings-panel">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Prestataire</th>
                  <th className="text-left px-4 py-3 font-semibold">Tâches</th>
                  <th className="text-left px-4 py-3 font-semibold">Montant</th>
                  <th className="text-left px-4 py-3 font-semibold">Commission</th>
                  <th className="text-left px-4 py-3 font-semibold">Paiement</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">
                      <div className="font-medium">{b.date}</div>
                      <div className="text-xs text-gray-400">{b.time}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{b.provider_name}</div>
                      <div className="text-xs text-gray-500">{b.provider_skill}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{b.hours}</td>
                    <td className="px-4 py-3 font-semibold">{b.subtotal} DH</td>
                    <td className="px-4 py-3 text-orange-600 font-medium">{b.total_commission} DH</td>
                    <td className="px-4 py-3 text-xs capitalize">{b.payment_method}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{b.status}</span>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Aucune réservation</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Reviews */}
        {tab === 'reviews' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100" data-testid="reviews-panel">
            <div className="divide-y divide-gray-100">
              {reviews.map((r) => (
                <div key={r.id} className="p-5 flex items-start gap-4" data-testid={`review-row-${r.id}`}>
                  <img src={r.provider_photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-gray-900">{r.reviewer_name}</span>
                      <span className="text-xs text-gray-400">sur {r.provider_name}</span>
                      <div className="flex items-center gap-0.5 ml-auto">
                        <Star size={12} className="text-yellow-400 fill-yellow-400" />
                        <span className="text-xs font-bold">{r.rating}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{r.text}</p>
                    <p className="text-xs text-gray-400 mt-1">{r.time_ago}</p>
                  </div>
                  <button
                    onClick={() => deleteReview(r.id)}
                    data-testid={`delete-review-${r.id}`}
                    className="text-red-500 hover:bg-red-50 p-2 rounded-lg flex-shrink-0"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {reviews.length === 0 && (
                <div className="px-4 py-10 text-center text-gray-500">Aucun avis</div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminPage;
